import {
  useCallback, useContext, useEffect, useState
} from "react";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import { Button, LoadingIndicator } from "@k8slens/lds";

import { Business } from "lens-platform-sdk";
import pick from "lodash/pick";

import { ProfileContext } from "src/providers/profile-provider";
import { useLogout } from "src/hooks/useLogout";
import { useGetBusiness } from "src/hooks/useGetBusiness";
import { NewBusiness } from "src/hooks/useCreateBusiness";
import { useUpdateBusiness } from "src/hooks/useUpdateBusiness";
import { useAnalytics } from "src/hooks/useAnalytics";

import Layout from "src/components/Layout/Layout";
import BusinessIdForm from "src/components/BusinessIdForm/BusinessIdForm";
import Avatar from "src/components/Avatar/Avatar";
import Notification from "src/components/Notification/Notification";

import styles from "./BusinessProfile.module.css";

const updateButtonLabel = "Save";

const BusinessProfile = () => {
  const { trackButtonClicked, track, trackError } = useAnalytics();

  const { businessId } = useParams<{ businessId?: string }>();
  const { updateBusiness, error, updating } = useUpdateBusiness();
  const [formValid, setFormValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const logout = useLogout();
  const { profile } = useContext(ProfileContext);

  const [business, setBusiness] = useState<NewBusiness>();

  const { business: initialBusiness, loading: loadingBusiness, error: getBusinessError } = useGetBusiness(businessId);

  const handleChange = useCallback((formData: Omit<Business, "id" | "createdAt" | "updatedAt" | "businessUsers" | "external" | "businessIdLiteSubscriptionId">, valid: boolean) => {
    setFormValid(valid);

    setBusiness({
      ...(pick(initialBusiness, ["id", "createdAt", "updatedAt"]) || {}),
      ...formData
    });
  }, [initialBusiness]);

  const saveBusiness = async () => {
    if (!business || !businessId) {
      return;
    }

    trackButtonClicked(updateButtonLabel);

    setSuccess(false);

    const updated = await updateBusiness(businessId, business);

    if (updated) {
      track("BusinessID profile updated", { businessId });
      setSuccess(true);
      setBusiness(updated);
    } else {
      trackError("BusinessID profile update failed", { businessId });
    }
  };

  useEffect(() => {
    if (initialBusiness && !business) {
      setBusiness(initialBusiness);
    }
  }, [initialBusiness, business]);

  return (
    <Layout className="bg-secondary" profile={profile} logout={logout} business={initialBusiness}>
      {
        profile && (
        <div className={styles.businessProfile}>
          {
              error && <Notification flashDuration={1000} level="error" message={error.message} type="flash" />
          }
          {
              success && <Notification flashDuration={1000} level="success" message="Profile updated" type="flash" />
          }
          <header className={styles.heading}>
            <h2 className={clsx("lds-h2-caps", styles.welcome)}>Profile</h2>
            <p>
              Manage your Lens Business ID
              {" "}
              <strong>{`(${businessId})`}</strong>
              {" "}
              profile information in here.
            </p>
          </header>
          <section className={styles.content}>
            {
              initialBusiness && (
                <>
                  <BusinessIdForm
                    title="Edit your new Lens Business ID details."
                    onChange={handleChange}
                    error={getBusinessError || undefined}
                    initialData={initialBusiness}
                    singleColumn
                  />
                  <div className={styles.editAvatar}>
                    <span>Profile Image</span>
                    <Avatar name={business?.name || initialBusiness.name} className={styles.avatar} />
                  </div>
                  <div>
                    <Button disabled={!formValid} loading={loadingBusiness || updating} label={updateButtonLabel} id="save" onClick={saveBusiness} primary />
                  </div>
                </>
              )
            }
          </section>
          {
            (updating || loadingBusiness) && <LoadingIndicator size="xxl" className={styles.loadingIndicator} />
          }
        </div>
        )
      }
    </Layout>
  );
};

export default BusinessProfile;
